/**
 * Opportunity Detail Panel
 * Shows enhanced opportunity fields (referral tracking, relationship type,
 * prelim context, needs overview) and tabs for Documents, Fee Schedule,
 * Meeting Intelligence, and Org Activation.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  X,
  Building2,
  User,
  FileText,
  DollarSign,
  Brain,
  Rocket,
  Calendar,
  ListChecks,
  Tag,
  Save,
  Loader2,
  CheckCircle,
  AlertCircle,
} from 'lucide-react';
import {
  opportunitiesAPI,
  Opportunity,
  ProspectRelationshipType,
} from '@/services/api/salesPipelineAPI';
import { DocumentsPanel } from './DocumentsPanel';
import { FeeSchedulePanel } from './FeeSchedulePanel';
import { MeetingIntelligencePanel } from './MeetingIntelligencePanel';
import { OrgActivationPanel } from './OrgActivationPanel';
import { OpportunityActivitiesPanel } from './OpportunityActivitiesPanel';

interface OpportunityDetailPanelProps {
  opportunityId: string;
  onClose: () => void;
  onUpdated?: () => void;
}

type DetailTab = 'overview' | 'activity' | 'documents' | 'fees' | 'meetings' | 'activation';

const RELATIONSHIP_LABELS: Record<ProspectRelationshipType, string> = {
  sponsor_org: 'Sponsor Organization',
  cbo: 'Community-Based Organization',
  strategic_partner: 'Strategic Partner',
  advisor: 'Advisor',
  investor: 'Investor',
  other: 'Other',
};

export const OpportunityDetailPanel: React.FC<OpportunityDetailPanelProps> = ({
  opportunityId,
  onClose,
  onUpdated,
}) => {
  const [opportunity, setOpportunity] = useState<Opportunity | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<DetailTab>('overview');
  const [editMode, setEditMode] = useState(false);

  // Editable fields
  const [referredByName, setReferredByName] = useState('');
  const [referralSource, setReferralSource] = useState('');
  const [prospectRelationshipType, setProspectRelationshipType] = useState<ProspectRelationshipType | ''>('');
  const [prelimContext, setPrelimContext] = useState('');
  const [needsOverview, setNeedsOverview] = useState('');
  const [demoCompleted, setDemoCompleted] = useState(false);

  const loadOpportunity = useCallback(async () => {
    setLoading(true);
    try {
      const opp = await opportunitiesAPI.getOpportunity(opportunityId);
      if (opp) {
        setOpportunity(opp);
        setReferredByName(opp.referredByName || '');
        setReferralSource(opp.referralSource || '');
        setProspectRelationshipType(opp.prospectRelationshipType || '');
        setPrelimContext(opp.prelimContext || '');
        setNeedsOverview(opp.needsOverview || '');
        setDemoCompleted(opp.demoCompleted || false);
      }
    } catch (err) {
      console.error('Failed to load opportunity:', err);
    } finally {
      setLoading(false);
    }
  }, [opportunityId]);

  useEffect(() => {
    loadOpportunity();
  }, [loadOpportunity]);

  const handleSave = async () => {
    if (!opportunity) return;
    setSaving(true);
    try {
      await opportunitiesAPI.updateOpportunity(opportunity.id, {
        ...opportunity,
        referredByName: referredByName || undefined,
        referralSource: referralSource || undefined,
        prospectRelationshipType: prospectRelationshipType || undefined,
        prelimContext: prelimContext || undefined,
        needsOverview: needsOverview || undefined,
        demoCompleted,
      });
      setEditMode(false);
      onUpdated?.();
      await loadOpportunity();
    } catch (err) {
      console.error('Failed to save opportunity:', err);
    } finally {
      setSaving(false);
    }
  };

  const tabs: { id: DetailTab; label: string; icon: React.ReactNode }[] = [
    { id: 'overview', label: 'Overview', icon: <Building2 className="w-4 h-4" /> },
    { id: 'activity', label: 'Activity', icon: <ListChecks className="w-4 h-4" /> },
    { id: 'documents', label: 'Documents', icon: <FileText className="w-4 h-4" /> },
    { id: 'fees', label: 'Deal Estimate', icon: <DollarSign className="w-4 h-4" /> },
    { id: 'meetings', label: 'Meeting Intel', icon: <Brain className="w-4 h-4" /> },
    { id: 'activation', label: 'Activation', icon: <Rocket className="w-4 h-4" /> },
  ];

  if (loading) {
    return (
      <div className="bg-white rounded-lg border shadow-lg p-6">
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-blue-600 mr-2" />
          <span className="text-gray-500">Loading opportunity...</span>
        </div>
      </div>
    );
  }

  if (!opportunity) {
    return (
      <div className="bg-white rounded-lg border shadow-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <span className="text-red-500">Opportunity not found</span>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border shadow-lg overflow-hidden">
      {/* Header */}
      <div className="px-3 sm:px-6 py-3 sm:py-4 border-b bg-gray-50 flex items-start sm:items-center justify-between gap-2">
        <div className="min-w-0 flex-1">
          <h2 className="text-base sm:text-lg font-semibold text-gray-900 truncate">{opportunity.name}</h2>
          <div className="flex flex-wrap items-center gap-2 sm:gap-3 mt-1 text-sm text-gray-500">
            <span className="flex items-center gap-1 truncate"><Building2 className="w-3.5 h-3.5 flex-shrink-0" />{opportunity.company}</span>
            <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">{opportunity.stage}</span>
            <span>${(opportunity.amount || 0).toLocaleString()}</span>
          </div>
        </div>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600 flex-shrink-0 p-1"><X className="w-5 h-5" /></button>
      </div>

      {/* Tabs */}
      <div className="border-b px-3 sm:px-6 overflow-x-auto">
        <div className="flex gap-1 -mb-px min-w-max">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-1 sm:gap-1.5 px-2 sm:px-3 py-2.5 text-xs sm:text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                activeTab === tab.id
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.icon}
              <span className="hidden sm:inline">{tab.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      <div className="p-3 sm:p-6 max-h-[70vh] sm:max-h-[600px] overflow-y-auto">
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* Enhanced Fields Section */}
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider">Sales Enhancement Details</h3>
              {!editMode ? (
                <button onClick={() => setEditMode(true)} className="text-sm text-blue-600 hover:text-blue-800">Edit</button>
              ) : (
                <div className="flex gap-2">
                  <button onClick={() => setEditMode(false)} className="text-sm text-gray-500 hover:text-gray-700">Cancel</button>
                  <button onClick={handleSave} disabled={saving} className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800">
                    {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                    Save
                  </button>
                </div>
              )}
            </div>

            {/* Referral Tracking */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Referred By</label>
                {editMode ? (
                  <input
                    type="text"
                    value={referredByName}
                    onChange={(e) => setReferredByName(e.target.value)}
                    placeholder="Contact name"
                    className="w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                ) : (
                  <p className="text-sm text-gray-900">{referredByName || <span className="text-gray-400 italic">None</span>}</p>
                )}
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Referral Source</label>
                {editMode ? (
                  <input
                    type="text"
                    value={referralSource}
                    onChange={(e) => setReferralSource(e.target.value)}
                    placeholder="How they found us"
                    className="w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                ) : (
                  <p className="text-sm text-gray-900">{referralSource || <span className="text-gray-400 italic">None</span>}</p>
                )}
              </div>
            </div>

            {/* Relationship Type */}
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Prospect Relationship Type</label>
              {editMode ? (
                <select
                  value={prospectRelationshipType}
                  onChange={(e) => setProspectRelationshipType(e.target.value as ProspectRelationshipType)}
                  className="w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select...</option>
                  {Object.entries(RELATIONSHIP_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
              ) : (
                <div className="flex items-center gap-1.5">
                  <Tag className="w-3.5 h-3.5 text-gray-400" />
                  <span className="text-sm text-gray-900">
                    {prospectRelationshipType ? RELATIONSHIP_LABELS[prospectRelationshipType] : <span className="text-gray-400 italic">Not set</span>}
                  </span>
                </div>
              )}
            </div>

            {/* Demo Completed */}
            <div className="flex items-center gap-2">
              {editMode ? (
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={demoCompleted}
                    onChange={(e) => setDemoCompleted(e.target.checked)}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700">Demo Completed</span>
                </label>
              ) : (
                <div className="flex items-center gap-1.5">
                  {demoCompleted ? (
                    <CheckCircle className="w-4 h-4 text-green-500" />
                  ) : (
                    <AlertCircle className="w-4 h-4 text-gray-300" />
                  )}
                  <span className="text-sm text-gray-700">Demo {demoCompleted ? 'Completed' : 'Not Completed'}</span>
                </div>
              )}
            </div>

            {/* Pre-meeting Context */}
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Pre-Meeting Context / Prelim Info</label>
              {editMode ? (
                <textarea
                  value={prelimContext}
                  onChange={(e) => setPrelimContext(e.target.value)}
                  rows={3}
                  placeholder="What we know about the prospect before first meeting..."
                  className="w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              ) : (
                <p className="text-sm text-gray-900 whitespace-pre-wrap">{prelimContext || <span className="text-gray-400 italic">No context recorded</span>}</p>
              )}
            </div>

            {/* Needs Overview */}
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Needs Overview</label>
              {editMode ? (
                <textarea
                  value={needsOverview}
                  onChange={(e) => setNeedsOverview(e.target.value)}
                  rows={3}
                  placeholder="Overview of prospect needs and opportunity..."
                  className="w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              ) : (
                <p className="text-sm text-gray-900 whitespace-pre-wrap">{needsOverview || <span className="text-gray-400 italic">No needs recorded</span>}</p>
              )}
            </div>

            {/* Core Opportunity Info Summary */}
            <div className="border-t pt-4 mt-4">
              <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-3">Opportunity Summary</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                <div><span className="text-gray-500">Owner:</span> <span className="text-gray-900 ml-1">{opportunity.owner}</span></div>
                <div><span className="text-gray-500">Source:</span> <span className="text-gray-900 ml-1">{opportunity.source || 'N/A'}</span></div>
                <div><span className="text-gray-500">Close Date:</span> <span className="text-gray-900 ml-1">{opportunity.closeDate || 'N/A'}</span></div>
                <div><span className="text-gray-500">Probability:</span> <span className="text-gray-900 ml-1">{opportunity.probability}%</span></div>
                <div><span className="text-gray-500">Created:</span> <span className="text-gray-900 ml-1">{opportunity.createdAt ? new Date(opportunity.createdAt).toLocaleDateString() : 'N/A'}</span></div>
                <div><span className="text-gray-500">Last Activity:</span> <span className="text-gray-900 ml-1">{opportunity.lastActivity ? new Date(opportunity.lastActivity).toLocaleDateString() : 'N/A'}</span></div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'activity' && (
          <OpportunityActivitiesPanel
            opportunityId={opportunityId}
            contactName={opportunity.company}
          />
        )}

        {activeTab === 'documents' && (
          <DocumentsPanel opportunityId={opportunityId} />
        )}

        {activeTab === 'fees' && (
          <FeeSchedulePanel opportunityId={opportunityId} />
        )}

        {activeTab === 'meetings' && (
          <MeetingIntelligencePanel opportunityId={opportunityId} />
        )}

        {activeTab === 'activation' && (
          <OrgActivationPanel
            opportunityId={opportunityId}
            opportunityStage={opportunity.stage}
            companyName={opportunity.company}
            companyId={opportunity.companyId}
          />
        )}
      </div>
    </div>
  );
};
